import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, Eyebrow } from "@/components/Reveal";
import { WorldMap } from "@/components/WorldMap";
import { countries } from "@/data/countries";

const regions = ["All", "Middle East", "Asia", "Africa"];
const tileStyles = {
  "Middle East": "bg-petal text-white",
  Asia: "bg-pine text-cream",
  Africa: "bg-pine-light text-ink",
};

export default function MapPage() {
  const [selected, setSelected] = useState(null);

  return (
    <div data-testid="map-page" className="pt-24 lg:pt-28 bg-cream min-h-screen grain relative">
      <Seo
        title="Interactive Map — Hijrah Mom"
        siteName="Hijrah Mom"
        description="Explore the Muslim world on an interactive map: profiled hijrah destinations across the Middle East, Africa, and Asia, each with a full family relocation profile."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-12 lg:py-16">
        <Eyebrow>The world, from a mother's eyes</Eyebrow>
        <h1 className="mt-4 font-serif font-bold text-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.05]">
          One ummah. <span className="italic text-petal">Multiple opportunities for hijrah.</span>
        </h1>
        <p className="mt-6 text-sage max-w-2xl leading-relaxed">
          Tap a pin to peek inside.
        </p>

        <FadeUp className="mt-12 bg-white rounded-3xl border border-pine/10 shadow-sm p-3 lg:p-6">
          <WorldMap interactive selected={selected} onSelect={setSelected} />
        </FadeUp>

        <AnimatePresence>
          {selected && (
            <motion.div
              key={selected.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.35 }}
              data-testid="map-quick-card"
              className="mt-6 bg-pine text-cream rounded-2xl p-6 lg:p-8 flex flex-col md:flex-row md:items-center gap-6 shadow-xl"
            >
              <div className="flex-1">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase font-semibold text-cream">{selected.region}</p>
                <h2 className="font-serif font-semibold text-3xl mt-1">{selected.name} <span className="italic text-xl text-sage-light">{selected.arabic}</span></h2>
                <p className="font-serif italic text-lg text-sage-light mt-2">{selected.tagline}</p>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to={`/countries/${selected.slug}`}
                  data-testid="map-quick-card-link"
                  className="inline-flex items-center gap-2 bg-petal text-white font-semibold px-6 py-3 rounded-full hover:bg-cream hover:text-pine transition-colors duration-300"
                >
                  Full profile <ArrowRight size={16} />
                </Link>
                <button
                  data-testid="map-quick-card-close"
                  onClick={() => setSelected(null)}
                  className="w-11 h-11 rounded-full border border-cream/25 flex items-center justify-center hover:bg-cream/10 transition-colors"
                  aria-label="Close preview"
                >
                  <X size={18} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-20 lg:pb-28">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <h2 className="font-serif font-semibold text-ink text-2xl lg:text-3xl">Browse the profiles</h2>
        </div>
        <div className="space-y-10" data-testid="map-country-groups">
          {regions.filter((r) => r !== "All").map((r) => (
            <div key={r} data-testid={`map-group-${r.toLowerCase().replace(/\s/g, "-")}`}>
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-pine-dark font-semibold mb-4 flex items-center gap-3">
                {r} <span className="h-px flex-1 bg-pine/15" />
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {countries.filter((c) => c.region === r).sort((x, y) => x.name.localeCompare(y.name)).map((c) => (
                  <Link
                    key={c.slug}
                    to={`/countries/${c.slug}`}
                    data-testid={`map-tile-${c.slug}`}
                    className={`group rounded-2xl px-4 py-4 flex items-center justify-between gap-3 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 ${tileStyles[r]}`}
                  >
                    <span className="font-serif font-semibold text-lg">{c.name}</span>
                    <ArrowRight size={16} className="shrink-0 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
